/* WeQuote Dev Board — app shell (rebuild). Reference side nav + top bar; view
 * routing; the shared card pop-up mounts once here so every view opens the
 * same pop-up (PRD §6). Live so far: Kanban (Phase 1). Other views are
 * phase-stubs until their phase lands. */
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './devboard.css'
import { WEQUOTE_LOGO_SVG } from './logo'
import { useAppMode } from '../../../contexts/AppModeContext'
import { useAuth } from '../../../contexts/AuthContext'
import {
  IcOverview, IcRoadmap, IcCalendar, IcFlag, IcList, IcBoard, IcCheckCircle,
  IcArchive, IcBulb, IcChevronLeft, IcChevronRight, IcPlus, IcBell, IcMoon, IcSun, IcUser, IcMegaphone,
} from './icons'
import { DevBoardProvider, useDevBoard, type View } from './DevBoardProvider'
import { SearchBar } from './views/SearchBar'
import { BoardView } from './views/BoardView'
import { CardModal } from './views/CardModal'
import { NotifySettingsModal } from './views/NotifySettings'
import { PriorityView } from './views/PriorityView'
import { ReviewView } from './views/ReviewView'
import { ArchivedView } from './views/ArchivedView'
import { RoadmapView } from './views/RoadmapView'
import { CalendarView } from './views/CalendarView'
import { OverviewView } from './views/OverviewView'
import { MyBoardView } from './views/MyBoardView'
import { ReleaseNotesView } from './views/ReleaseNotes'
import { CompletedView } from './views/CompletedView'
import { IdeasView } from './views/IdeasView'

interface NavDef { view: View; label: string; ico: React.ReactNode; badge?: 'review' | 'priority' | 'archived' | 'ideas'; badgeGrey?: boolean }
const NAV: NavDef[] = [
  { view: 'overview', label: 'Overview', ico: <IcOverview /> },
  { view: 'myboard', label: 'My Board', ico: <IcUser /> }, // v2 §4
  { view: 'roadmap', label: 'Roadmap', ico: <IcRoadmap /> },
  { view: 'calendar', label: 'Calendar', ico: <IcCalendar /> },
  { view: 'review', label: 'Needs Review', ico: <IcFlag />, badge: 'review' },
  { view: 'detail', label: 'Priority List', ico: <IcList />, badge: 'priority', badgeGrey: true },
  { view: 'board', label: 'Kanban Board', ico: <IcBoard /> },
  { view: 'completed', label: 'Completed', ico: <IcCheckCircle /> },
  { view: 'relnotes', label: 'Release Notes', ico: <IcMegaphone /> }, // Lee, 3 Sep
  { view: 'archived', label: 'Archived', ico: <IcArchive />, badge: 'archived', badgeGrey: true },
  { view: 'ideas', label: 'Ideas', ico: <IcBulb />, badge: 'ideas', badgeGrey: true },
]

const TITLES: Record<View, [string, string]> = {
  overview: ['Overview', 'Dev Board · 2026–27 · Draft'],
  myboard: ['My Board', 'Your own cards, stages and schedule'],
  relnotes: ['Release Notes', 'The customer-facing updates page + embed'],
  roadmap: ['Roadmap', 'Per-developer timeline · Draft'],
  calendar: ['Calendar', 'Day / week / month · Draft'],
  review: ['Needs Review', 'Intake queue · Draft'],
  detail: ['Priority List', 'Platform Modernisation · Draft'],
  list: ['Priority List', 'Platform Modernisation · Draft'],
  board: ['Kanban Board', 'Delivery flow · Draft'],
  completed: ['Completed', 'Live features & announcements · Draft'],
  archived: ['Archived', 'Closed with a reason · Draft'],
  ideas: ['Ideas', 'Inspiration → Backlog · Draft'],
}
const DevBoardInner: React.FC = () => {
  const { loading, error, view, setView, navCollapsed, toggleNav, badges, toast, openSubModal } = useDevBoard()
  const { requestSwitch, forceSupport } = useAppMode()
  const { currentAgent } = useAuth()
  const navigate = useNavigate()
  const [notifyOpen, setNotifyOpen] = useState(false)
  /* Dark mode (Lee, 26 Aug) — per-user, remembered like view/collapse state. */
  const themeKey = 'wq-devboard-theme:' + ((currentAgent as any)?.id || 'anon')
  const [dark, setDark] = useState<boolean>(() => {
    try { return localStorage.getItem(themeKey) === 'dark' } catch { return false }
  })
  const toggleTheme = () => setDark((d) => {
    const next = !d
    try { localStorage.setItem(themeKey, next ? 'dark' : 'light') } catch { /* per-user convenience only */ }
    return next
  })

  useEffect(() => {
    const id = 'wq-atkinson-font'
    if (!document.getElementById(id)) {
      const l = document.createElement('link')
      l.id = id
      l.rel = 'stylesheet'
      l.href = 'https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:wght@400;700&display=swap'
      document.head.appendChild(l)
    }
  }, [])

  const onPriority = view === 'detail' || view === 'list'
  const [title, sub] = TITLES[view]
  const body = loading
    ? <div style={{ padding: 40, color: '#6b7280' }}>Loading the board…</div>
    : error
      ? <div style={{ padding: 40, color: '#dc2626' }}>Couldn’t load the board: {error}</div>
      : view === 'relnotes'
        ? <ReleaseNotesView />
        : view === 'myboard'
        ? <MyBoardView />
        : view === 'board'
        ? <BoardView />
        : onPriority
          ? <PriorityView tab={view === 'list' ? 'list' : 'detail'} setTab={(t) => setView(t)} />
          : view === 'review'
            ? <ReviewView />
            : view === 'archived'
              ? <ArchivedView />
              : view === 'roadmap'
                ? <RoadmapView />
                : view === 'calendar'
                  ? <CalendarView />
                  : view === 'overview'
                    ? <OverviewView />
                    : view === 'completed'
                      ? <CompletedView />
                      : <IdeasView />

  return (
    <div className={'wq-devboard' + (dark ? ' theme-dark' : '')}>
      <div className={'app' + (navCollapsed ? ' nav-collapsed' : '')}>
        <nav className="sidenav">
          <div className="sidenav-logo">
            <span dangerouslySetInnerHTML={{ __html: WEQUOTE_LOGO_SVG }} />
            <div className="sidenav-sub">Dev Board · 2026–27 · Draft</div>
          </div>
          <div className="nav-items">
            {NAV.map((n) => {
              const active = n.view === 'detail' ? onPriority : view === n.view
              const count = n.badge ? badges[n.badge] : 0
              return (
                <button key={n.view} className={'nav-item' + (active ? ' active' : '')} onClick={() => setView(n.view)}>
                  <span className="ico">{n.ico}</span> {n.label}
                  {n.badge && count > 0 && <span className={'nav-badge' + (n.badgeGrey ? ' grey' : '')}>{count}</span>}
                </button>
              )
            })}
          </div>
        </nav>
        <button className="nav-toggle" onClick={toggleNav} title="Collapse / expand menu">{navCollapsed ? <IcChevronRight size={14} /> : <IcChevronLeft size={14} />}</button>
        <div className="main">
          <div className="main-top">
            <div><h1>{title}</h1><div className="sub">{sub}</div></div>
            <div className="top-actions">
              <SearchBar />
              <button className="mode-btn icon-only" title={dark ? 'Light mode' : 'Dark mode'} onClick={toggleTheme}>{dark ? <IcSun size={14} /> : <IcMoon size={14} />}</button>
              <button className="mode-btn icon-only" title="Email notifications" onClick={() => setNotifyOpen(true)}><IcBell size={14} /></button>
              {/* Profile — reachable from Dev Mode (switches to Support and opens
                  the profile page, which lives in the Support shell). */}
              <button className="mode-btn icon-only" title="Profile" onClick={() => { forceSupport(); navigate('/profile') }}><IcUser size={14} /></button>
              {/* Everyone can switch modes now. */}
              <button className="mode-btn" onClick={() => requestSwitch('support')}>Support Mode</button>
              <button className="add-btn" onClick={() => openSubModal({ kind: 'addFeature' })}><IcPlus size={13} /> Add Feature</button>
            </div>
          </div>
          {body}
        </div>
      </div>
      <CardModal />
      {notifyOpen && <NotifySettingsModal onClose={() => setNotifyOpen(false)} />}
      {toast && <div className="toast show">{toast}</div>}
    </div>
  )
}

export const DevBoard: React.FC = () => (
  <DevBoardProvider>
    <DevBoardInner />
  </DevBoardProvider>
)
